import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { 
  ArrowLeft, 
  Edit, 
  Settings, 
  Trash2, 
  Power, 
  Wifi, 
  Clock, 
  Battery, 
  HardDrive 
} from 'lucide-react';
import { mockKioskDevices, mockKioskSettings } from '../data/mock-kiosk-data';
import { KioskDevice, KioskSettings } from '../types';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Simple progress bar component
const ProgressBar = ({ value, className }: { value: number, className?: string }) => (
  <div className={`w-full bg-secondary rounded-full h-2 ${className}`}>
    <div 
      className="bg-primary h-full rounded-full" 
      style={{ width: `${value}%` }}
    />
  </div>
);

export function KioskDeviceDetailsPage() {
  const { id } = useParams({ from: '/_authenticated/kiosk/devices/$id' });
  const [device, setDevice] = useState<KioskDevice | null>(null);
  const [settings, setSettings] = useState<KioskSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    // In a real app, this would be an API call
    const foundDevice = mockKioskDevices.find(d => d.id === id);
    setDevice(foundDevice || null);
    
    if (foundDevice) {
      const foundSettings = mockKioskSettings.find(s => s.id === foundDevice.settingsId);
      setSettings(foundSettings || null);
    }
    
    setLoading(false);
  }, [id]);
  
  const handleReboot = () => {
    alert('Device reboot command sent!');
  };
  
  const handleDelete = () => {
    const confirmed = window.confirm('Are you sure you want to delete this device?');
    if (confirmed) {
      alert('Device deleted successfully!');
      navigate({ to: '/kiosk/devices', search: { settingsId: undefined } });
    }
  };
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!device) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Device Not Found"
          description="The requested kiosk device could not be found."
          actions={
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/devices" search={{ settingsId: undefined }}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Devices
              </Link>
            </Button>
          }
        />
        <Card>
          <CardContent className="pt-6">
            <p>The kiosk device with ID "{id}" could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'offline':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };
  
  return (
    <>
      <PageHeader
        title={device.name}
        description={device.description}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/devices" search={{ settingsId: undefined }}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Link>
            </Button>
            {settings && (
              <Button asChild variant="outline" size="sm">
                <Link to="/kiosk/settings/$id" params={{ id: device.settingsId }}>
                  <Settings className="mr-2 h-4 w-4" />
                  View Settings
                </Link>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleReboot}>
              <Power className="mr-2 h-4 w-4" />
              Reboot
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/devices/edit/$id" params={{ id: device.id }} search={{ settingsId: undefined }}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          </>
        }
      />
      
      <div className="grid gap-4 md:grid-cols-4 mb-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge 
              className={getStatusColor(device.status)}
              variant="outline"
            >
              {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Last Ping</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium">
              {formatDistanceToNow(new Date(device.lastPing * 1000), { addSuffix: true })}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Settings Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium">
              {settings ? settings.name : 'None'}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Location</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium">{device.location}</div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Device Details</TabsTrigger>
          <TabsTrigger value="network">Network</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Hardware Information</CardTitle>
              <CardDescription>
                Technical specifications of this kiosk device.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Model</h4>
                  <p>{device.model}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Serial Number</h4>
                  <p>{device.serialNumber}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Screen Size</h4>
                  <p>{device.screenSize}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Orientation</h4>
                  <p className="capitalize">{device.orientation}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">OS Version</h4>
                  <p>{device.osVersion}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Last Updated</h4>
                  <p>{formatDistanceToNow(new Date(device.lastUpdated * 1000), { addSuffix: true })}</p>
                </div>
              </div>
              
              {device.notes && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Notes</h4>
                  <p className="text-sm text-muted-foreground">{device.notes}</p>
                </div>
              )}
              
              {device.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Tags</h4>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {device.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="capitalize">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="network">
          <Card>
            <CardHeader>
              <CardTitle>Network Information</CardTitle>
              <CardDescription>
                Network configuration and connectivity details.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">IP Address</h4>
                  <p>{device.ipAddress}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">MAC Address</h4>
                  <p>{device.macAddress}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Connection Status</h4>
                  <div className="flex items-center gap-2">
                    <Wifi className={`h-4 w-4 ${device.status === 'online' ? 'text-green-500' : 'text-red-500'}`} />
                    <span className={device.status === 'online' ? 'text-green-500' : 'text-red-500'}>
                      {device.status === 'online' ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Last Connection</h4>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{formatDistanceToNow(new Date(device.lastPing * 1000), { addSuffix: true })}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle>System Status</CardTitle>
              <CardDescription>
                Current system status and resource usage.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium">Battery</h4>
                  <span className="text-sm">{device.batteryLevel}%</span>
                </div>
                <div className="flex items-center gap-4">
                  <Battery className="h-4 w-4 text-muted-foreground" />
                  <ProgressBar 
                    value={device.batteryLevel} 
                    className={
                      device.batteryLevel > 50 ? 'bg-green-100' : 
                      device.batteryLevel > 20 ? 'bg-yellow-100' : 
                      'bg-red-100'
                    }
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium">Storage</h4>
                  <span className="text-sm">{device.storageUsed}% used</span>
                </div>
                <div className="flex items-center gap-4">
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                  <ProgressBar 
                    value={device.storageUsed} 
                    className={
                      device.storageUsed < 50 ? 'bg-green-100' : 
                      device.storageUsed < 80 ? 'bg-yellow-100' : 
                      'bg-red-100'
                    }
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">OS Version</h4>
                  <p>{device.osVersion}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Last Reboot</h4>
                  <p>Unknown</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <div className="mt-6">
        <Button variant="destructive" size="sm" onClick={handleDelete}>
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Device
        </Button>
      </div>
    </>
  );
}

export default KioskDeviceDetailsPage;
